/**
 * CampaignView - View a specific Campaign Pack from library
 * Reuses BoosterHome logic but loads specific pack by ID
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import PanelLayout from '@/components/PanelLayout';
import { Button } from '@/components/ui/button';
import ImageCropModal from '@/components/ImageCropModal';
import { API } from '@/utils/api';
import {
  ArrowLeft, Copy, Loader2, CheckCircle2,
  Facebook, Instagram, Target, Flame,
  Download, ImageIcon, Send, Crop,
  ThumbsUp, MessageCircle, Share2, Globe, Calendar
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CARD_META = {
  facebook_post: { icon: Facebook, color: '#1877F2', bg: '#EBF5FF', label: 'Facebook', platform: 'facebook' },
  instagram_caption: { icon: Instagram, color: '#E1306C', bg: '#FDF2F8', label: 'Instagram', platform: 'instagram' },
  cta: { icon: Target, color: '#F59E0B', bg: '#FFFBEB', label: 'Call to Action', platform: 'other' },
  promo_idea: { icon: Flame, color: '#10B981', bg: '#ECFDF5', label: 'Pomysł promocji', platform: 'other' },
};

function formatDate(isoStr) {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}

function FbPreviewCard({ card, businessName }) {
  const initial = (businessName || 'A').charAt(0).toUpperCase();
  return (
    <div className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: '#e4e6eb' }}>
      <div className="flex items-center gap-2.5 px-4 pt-3.5 pb-2">
        <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ background: '#1877F2' }}>{initial}</div>
        <div className="flex-1">
          <p className="text-[13px] font-semibold leading-tight" style={{ color: '#050505' }}>{businessName || 'Twoja firma'}</p>
          <div className="flex items-center gap-1 mt-0.5">
            <span className="text-[11px]" style={{ color: '#65676B' }}>Sponsorowane</span>
            <span className="text-[11px]" style={{ color: '#65676B' }}>·</span>
            <Globe className="w-2.5 h-2.5" style={{ color: '#65676B' }} />
          </div>
        </div>
      </div>
      <div className="px-4 pb-2.5">
        <p className="text-[14px] leading-[1.35] whitespace-pre-line" style={{ color: '#050505' }}>{card.content}</p>
        {card.hashtags && <p className="text-[13px] mt-1.5" style={{ color: '#1877F2' }}>{card.hashtags}</p>}
      </div>
      {card.image_url && (
        <div className="relative">
          <img src={card.image_url} alt={card.image_alt || ''} className="w-full object-cover" style={{ maxHeight: '420px' }} loading="lazy" />
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: 'rgba(0,0,0,0.6)' }}>Podgląd reklamy</div>
        </div>
      )}
      {card.extra?.button_text && (
        <div className="px-4 py-2.5 border-t flex items-center justify-between" style={{ borderColor: '#e4e6eb', background: '#F0F2F5' }}>
          <div>
            {card.extra?.headline && <p className="text-[13px] font-semibold" style={{ color: '#050505' }}>{card.extra.headline}</p>}
          </div>
          <div className="px-3 py-1.5 rounded-md text-[13px] font-semibold" style={{ background: '#e4e6eb', color: '#050505' }}>{card.extra.button_text}</div>
        </div>
      )}
      <div className="px-4 py-2 border-t" style={{ borderColor: '#e4e6eb' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            <div className="w-[18px] h-[18px] rounded-full flex items-center justify-center" style={{ background: '#1877F2' }}><ThumbsUp className="w-2.5 h-2.5 text-white" /></div>
            <span className="text-[12px] ml-1" style={{ color: '#65676B' }}>24</span>
          </div>
          <span className="text-[12px]" style={{ color: '#65676B' }}>3 komentarze</span>
        </div>
      </div>
      <div className="grid grid-cols-3 border-t" style={{ borderColor: '#e4e6eb' }}>
        {[{ icon: ThumbsUp, label: 'Lubię to' }, { icon: MessageCircle, label: 'Komentarz' }, { icon: Share2, label: 'Udostępnij' }].map(({ icon: Ic, label }) => (
          <div key={label} className="flex items-center justify-center gap-1.5 py-2.5 cursor-default hover:bg-gray-50">
            <Ic className="w-4 h-4" style={{ color: '#65676B' }} />
            <span className="text-[13px] font-medium" style={{ color: '#65676B' }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CampaignView() {
  const { t } = useTranslation();
  const { packId } = useParams();
  const navigate = useNavigate();
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [businessName, setBusinessName] = useState('');
  const [publishingCard, setPublishingCard] = useState(null);
  const [publishedCards, setPublishedCards] = useState({});
  const [cropCard, setCropCard] = useState(null);

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchPack();
    fetchBusinessName();
  }, [packId]);

  const fetchPack = async () => {
    try {
      const res = await axios.get(`${API}/booster/campaign-library/${packId}`, { headers: authHeaders });
      setPack(res.data);
    } catch { toast.error('Nie znaleziono kampanii'); navigate('/panel/biblioteka'); }
    finally { setLoading(false); }
  };

  const fetchBusinessName = async () => {
    try {
      const res = await axios.get(`${API}/customer-memory`, { headers: authHeaders });
      setBusinessName(res.data?.nazwa_firmy || '');
    } catch {}
  };

  const copyToClipboard = async (card) => {
    let text = card.content;
    if (card.hashtags) text += '\n\n' + card.hashtags;
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(card.type);
      toast.success('Skopiowano!');
      setTimeout(() => setCopiedId(null), 2000);
    } catch { toast.error('Błąd kopiowania'); }
  };

  const downloadImage = async (card) => {
    if (!card.image_url) return;
    try {
      const resp = await fetch(card.image_url);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `apisai-${card.type}.jpg`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Pobrano!');
    } catch { window.open(card.image_url, '_blank'); }
  };

  const publishToFacebook = async (card) => {
    setPublishingCard(card.type);
    try {
      const res = await axios.post(`${API}/integrations/facebook/publish`, {
        card_type: card.type, text: card.content, image_url: card.image_url || null, hashtags: card.hashtags || null,
      }, { headers: { ...authHeaders, 'Content-Type': 'application/json' } });
      setPublishedCards(prev => ({ ...prev, [card.type]: true }));
      toast.success(res.data.message || 'Opublikowano!');
    } catch (e) {
      const msg = e.response?.data?.detail?.message || 'Najpierw połącz Facebook w zakładce Booster';
      toast.error(msg);
    } finally { setPublishingCard(null); }
  };

  const handleCropSave = (croppedUrl) => {
    if (!cropCard) return;
    setPack(prev => ({ ...prev, cards: prev.cards.map(c => c.type === cropCard.type ? { ...c, image_url: croppedUrl } : c) }));
    toast.success('Skadrowano!');
    setCropCard(null);
  };

  if (loading) {
    return <PanelLayout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--apisai-primary)' }} /></div></PanelLayout>;
  }

  if (!pack) return null;

  return (
    <PanelLayout>
      <div className="max-w-3xl mx-auto" data-testid="campaign-view">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6">
          <button onClick={() => navigate('/panel/biblioteka')} className="flex items-center gap-1.5 text-xs font-medium mb-3 hover:underline" style={{ color: 'var(--apisai-primary)' }} data-testid="back-to-library">
            <ArrowLeft className="w-3.5 h-3.5" /> Biblioteka kampanii
          </button>
          <div className="flex items-center gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold" style={{ color: 'var(--apisai-text)' }}>Kampania marketingowa</h1>
                <span className="px-2 py-0.5 rounded-full text-[10px] font-medium" style={{ background: '#EBF5FF', color: '#1877F2' }}>AI Marketing Pack</span>
              </div>
              <div className="flex items-center gap-1.5 mt-1 text-xs" style={{ color: 'var(--apisai-text-muted)' }}>
                <Calendar className="w-3 h-3" />
                {formatDate(pack.created_at)}
                <span className="mx-1">·</span>
                {pack.cards?.length || 0} materiały
              </div>
            </div>
          </div>
        </motion.div>

        {/* Cards */}
        <div className="space-y-6 mb-8">
          {pack.cards?.map((card, i) => {
            const meta = CARD_META[card.type] || CARD_META.cta;
            const isCopied = copiedId === card.type;
            const isPublishing = publishingCard === card.type;
            const isPublished = publishedCards[card.type];
            const isSocial = meta.platform === 'facebook' || meta.platform === 'instagram';

            return (
              <motion.div key={card.type} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }} data-testid={`campaign-card-${card.type}`}>
                {isSocial ? (
                  <FbPreviewCard card={card} businessName={businessName} />
                ) : (
                  <div className="rounded-2xl border overflow-hidden" style={{ background: 'white', borderColor: 'var(--apisai-border)' }}>
                    {card.image_url && (
                      <div className="relative">
                        <img src={card.image_url} alt={card.image_alt || ''} className="w-full h-48 object-cover" loading="lazy" />
                        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-md text-[10px] font-semibold text-white" style={{ background: 'rgba(0,0,0,0.6)' }}>{meta.label}</div>
                      </div>
                    )}
                    <div className="px-5 py-4">
                      {card.extra?.headline && <p className="text-base font-bold mb-2" style={{ color: 'var(--apisai-text)' }}>{card.extra.headline}</p>}
                      <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--apisai-text)' }}>{card.content}</p>
                      {card.hashtags && <p className="text-xs mt-3 font-medium" style={{ color: meta.color }}>{card.hashtags}</p>}
                      {card.extra?.button_text && <div className="inline-block mt-3 px-4 py-2 rounded-lg text-sm font-medium" style={{ background: meta.color + '15', color: meta.color }}>{card.extra.button_text}</div>}
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex items-center flex-wrap gap-2 mt-2">
                  <Button variant="outline" onClick={() => copyToClipboard(card)} className="rounded-xl h-8 text-xs px-3 border bg-white" style={{ borderColor: isCopied ? '#10B981' : 'var(--apisai-border)', color: isCopied ? '#10B981' : 'var(--apisai-text-secondary)', background: isCopied ? '#F0FDF4' : 'white' }} data-testid={`copy-${card.type}`}>
                    {isCopied ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Skopiowano</> : <><Copy className="w-3.5 h-3.5 mr-1" /> Kopiuj tekst</>}
                  </Button>
                  {card.image_url && (
                    <>
                      <Button variant="outline" onClick={() => downloadImage(card)} className="rounded-xl h-8 text-xs px-3 border bg-white" style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }} data-testid={`download-btn-${card.type}`}>
                        <Download className="w-3.5 h-3.5 mr-1" /> Pobierz
                      </Button>
                      <Button variant="outline" onClick={() => setCropCard(card)} className="rounded-xl h-8 text-xs px-3 border bg-white" style={{ borderColor: 'var(--apisai-border)', color: 'var(--apisai-text-secondary)' }} data-testid={`crop-btn-${card.type}`}>
                        <Crop className="w-3.5 h-3.5 mr-1" /> Kadruj
                      </Button>
                    </>
                  )}
                  <Button onClick={() => publishToFacebook(card)} disabled={isPublishing || isPublished} className="rounded-xl h-8 text-xs px-3 font-medium text-white ml-auto" style={{ background: isPublished ? '#10B981' : '#1877F2' }} data-testid={`publish-fb-${card.type}`}>
                    {isPublishing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Publikuję...</> : isPublished ? <><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Opublikowano</> : <><Send className="w-3.5 h-3.5 mr-1" /> Publikuj na FB</>}
                  </Button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Crop Modal */}
        {cropCard && cropCard.image_url && <ImageCropModal imageUrl={cropCard.image_url} onSave={handleCropSave} onClose={() => setCropCard(null)} />}
      </div>
    </PanelLayout>
  );
}
